import { createPreference, fetchPreferenceByKey, PreferenceDto, updatePreference } from '@creditsights/api-ui-web';
import { useQuery, UseQueryResult } from 'react-query';
import { isBefore, compareDesc, subDays } from 'date-fns';
import { RecentSearch } from '../../models/RecentSearch';
import { queryKeys } from '../queryKeyFactory';

export const RECENT_SEARCHES_PREFERENCES_KEY = 'recentSearches';
export const REMOVE_RECENT_SEARCH_AFTER_DAYS = 14;

export default (): UseQueryResult<PreferenceDto<RecentSearch[]>, Error> =>
  useQuery<PreferenceDto<RecentSearch[]>, Error>([queryKeys.recentSearches.all], async () => {
    try {
      const recentSearches = await fetchPreferenceByKey<RecentSearch[]>(RECENT_SEARCHES_PREFERENCES_KEY);
      const removeRecentSearchBeforeDate = subDays(new Date(), REMOVE_RECENT_SEARCH_AFTER_DAYS);
      if (
        recentSearches.value.some((recentSearch) =>
          isBefore(new Date(recentSearch.searchedAt), removeRecentSearchBeforeDate)
        )
      ) {
        const filteredRecentSearches = removeOutdatedEntries(recentSearches, removeRecentSearchBeforeDate);
        const updatedRecentSearches = await updatePreference(recentSearches.id, {
          value: filteredRecentSearches.value,
        });

        return getSortedRecentSearches(updatedRecentSearches);
      }

      return getSortedRecentSearches(recentSearches);
    } catch (error: any) {
      if (error.request?.status === 404) {
        return createPreference({ key: RECENT_SEARCHES_PREFERENCES_KEY, value: [] });
      }

      throw error;
    }
  });

export const getSortedRecentSearches = (
  recentSearches: PreferenceDto<RecentSearch[]>
): PreferenceDto<RecentSearch[]> => ({
  ...recentSearches,
  value: recentSearches.value.sort((a, b) => compareDesc(new Date(a.searchedAt), new Date(b.searchedAt))),
});

const removeOutdatedEntries = (
  recentSearches: PreferenceDto<RecentSearch[]>,
  removeBefore: Date
): PreferenceDto<RecentSearch[]> => {
  return {
    ...recentSearches,
    value: recentSearches.value.filter((recentSearch) => !isBefore(new Date(recentSearch.searchedAt), removeBefore)),
  };
};
